// Sentry v8 folded @sentry/types and @sentry/tracing into the SDK; main is
// still on v7 where they are separate packages. Types come from
// @sentry/core here. #4166
import type { Event, Request, SpanJSON, TransactionEvent } from "@sentry/core";
import {
  sanitizeAlertData,
  sanitizeAlertIdentity,
  sanitizeDiagnosticReason,
  sanitizeSentryContexts,
  sanitizeString,
  sanitizeValue,
} from "./telemetrySanitizationHelpers";

export { sanitizeAlertData, sanitizeAlertIdentity, sanitizeDiagnosticReason };

const sanitizeHeaders = (headers: Request["headers"]): Request["headers"] => {
  if (!headers || typeof headers !== "object") {
    return headers;
  }

  return sanitizeValue(headers) as Request["headers"];
};

export const sanitizeSentryEvent = <T extends Event>(event: T): T => {
  if (event.message) {
    event.message = sanitizeString(event.message);
  }
  if (event.transaction) {
    event.transaction = sanitizeString(event.transaction);
  }

  if (event.request) {
    if (event.request.url) {
      event.request.url = sanitizeString(event.request.url);
    }
    if ("query_string" in event.request) {
      event.request.query_string = undefined;
    }
    if ("cookies" in event.request) {
      event.request.cookies = undefined;
    }
    if ("data" in event.request) {
      event.request.data = undefined;
    }
    if ("env" in event.request) {
      event.request.env = undefined;
    }
    if ("fragment" in event.request) {
      event.request.fragment = undefined;
    }
    event.request.headers = sanitizeHeaders(event.request.headers);
  }
  if ("user" in event) {
    event.user = undefined;
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = sanitizeString(exception.value);
    }
    exception.stacktrace?.frames?.forEach((frame) => {
      if (frame.filename) {
        frame.filename = sanitizeString(frame.filename);
      }
      if (frame.abs_path) {
        frame.abs_path = sanitizeString(frame.abs_path);
      }
      frame.vars = undefined;
    });
  });

  if (event.extra) {
    event.extra = sanitizeValue(event.extra) as Event["extra"];
  }
  if (event.contexts) {
    event.contexts = sanitizeSentryContexts(event.contexts);
  }
  if (event.tags) {
    event.tags = sanitizeValue(event.tags) as Event["tags"];
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
      ...breadcrumb,
      message: breadcrumb.message
        ? sanitizeString(breadcrumb.message)
        : breadcrumb.message,
      data: sanitizeValue(breadcrumb.data) as typeof breadcrumb.data,
    }));
  }

  return event;
};

export const sanitizeSentryTransaction = <T extends TransactionEvent>(
  event: T
): T => {
  sanitizeSentryEvent(event);
  event.spans?.forEach((span) => {
    if (span.description) {
      span.description = sanitizeString(span.description);
    }
    // v8 shape: event.spans are SpanJSON, which keeps `data` but has no
    // `tags` — v7's span tags were folded into span attributes/data. main
    // still scrubs both because it is on v7.
    span.data = sanitizeValue(span.data) as SpanJSON["data"];
  });

  return event;
};

export const debugLog = (...args: unknown[]): void => {
  if (process.env.NODE_ENV !== "production") {
    console.debug(...args.map((value) => sanitizeValue(value)));
  }
};
